import Promise from 'bluebird';
import _ from 'lodash';
import moment from 'moment';
import api from '../api';
import worldMap from '../core/map';
import { pinBuilder } from '../core/map/pinBuilder';
import templates from '../core/templates';
import ColorBrewer from '../core/tools/colorbrewer';
import ColorFlatUI from '../core/tools/colorflatui';
import { translateText } from '../core/tools/translate';
import environment from '../environment';
import Leaflet from './Leaflet';
import { createSymbologyScale } from './Leaflet/VectorLayer/VectorLayerUtils';
import store from '../../store';
import { OVERVIEW_PANEL_GET_CURRENT, OVERVIEW_PANEL_IS_VISIBLE } from '../../store/modules/overview-panel/types';
import { Dnc } from '../api/endpoints/dnc';
import { ContextEnum } from '../../enuns/context.enum';

// import 'mapbox.js';
const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);
const LayerControls = (name) => require('../controls').get(name);
const LayerInfo = (name) => require('../info').get(name);
const get = (name, currentMap = worldMap.getCurrent()) => {
    return require('./index').default.get(name, currentMap);
};

const name = 'DncFlexible';

class DncFlexibleLayer {
    constructor (currentMap) {
        let domain = [];
        let range = null;
        let scale = null;
        const mapId = +currentMap._id;

        const clusterTemplate = (features) => {
            const _getVoidPin = () =>
                L.icon({
                    iconUrl: L.DomUtil.create('canvas', '').toDataURL('image/png'),
                    iconSize: L.point(0, 0)
                });

            const { theme } = get(name, currentMap).options.symbology.cluster;
            if (!_.size(domain)) {
                const currentFeatures = get(name, currentMap).getCurrentFeatures();
                domain = _(currentFeatures).map('properties.Value').sortedUniq().value();
                if (!_.size(domain)) { return _getVoidPin(); }

                scale = createSymbologyScale(currentFeatures, {
                    value: 'Value',
                    legend: true,
                    symbology: { scale: 'jenks', dualband: true, theme }
                });

                if (!scale) { return _getVoidPin(); }

                range = scale.domain();
            }

            const value = _.sumBy(features.getAllChildMarkers(), 'feature.properties.Value');
            const color = value > _.max(range) ? scale(_.max(range)) : value < _.min(range) ? scale(_.min(range)) : scale(value);

            const data = {
                total: domain.reduce((acc, x) => acc + parseInt(x), 0),
                value,
                color
            };

            if (!data.value) {
                return _getVoidPin();
            }

            return pinBuilder.create.buffer.withValue(data);
        };

        const groupBy = (array, key) => {
            return array.reduce((acc, item) => ({
              ...acc,
              [item[key]]: [...(acc[item[key]] || []), item],
            }),
          {});
        };

        const onSuccess = (res) =>
            new Promise((resolve, reject) => {
                const features = res.features;
                const options = {
                    idTema: 23,
                    ids: _.map(features, f => f.properties.Id),
                    idTipo: LayerControls(name).getType(),
                    anoMesInicio: LayerControls(name).getDate(0).start.format('YYYYMM'),
                    anoMesFim: LayerControls(name).getDate(0).end.format('YYYYMM'),
                    periodo: environment.get('CONFIG.PARAM.DNC_DATES_PERIOD')
                };

                const filterData = LayerControls(name).getFilterControl(mapId);

                api.general.log.action(ContextEnum.LayerDncFlexible, 'Params', JSON.stringify(options));

                if (filterData.models) {
                    options.idsCategorias = filterData.models.value;
                } else if (filterData.competingClassification && _.size(filterData.competingClassification.value)) {
                    options.idsCategorias = _.flatMap(filterData.competingClassification.value, key => _.map(environment.get('CONFIG.FILTERS.COMPETING_SEGMENTS')[key], 'id'));
                }

                LayerControls(name).setLocaleTypeInfo(LayerControls(name).getType(), features[0].properties.Tipo);
             
                const geocodes = res.features
                    .map(f => f.properties.Geocodigo);
                const localeType = LayerControls(name).getType();
                const cityLocaleType = 3;
                const request = parseInt(localeType) === cityLocaleType
                    ? Dnc.getProjectByCity(environment.get('CONFIG.PARAM.DNC_DATES_PERIOD'), geocodes)
                    : Dnc.getProject(localeType, environment.get('CONFIG.PARAM.DNC_DATES_PERIOD'), geocodes);

                request.then(values => {
                     const period = {
                            start: moment(values[0].getInitialDate),
                            end: moment(values[0].getFinalDate)
                        };

                        const segments = filterData.models
                            ? filterData.models.text
                            : filterData.competingClassification && _.size(filterData.competingClassification.value)
                                ? _.flatMap(filterData.competingClassification.value, key => _.map(environment.get('CONFIG.FILTERS.COMPETING_SEGMENTS')[key], v => translateText(v.nome, true)))
                                : undefined;
                        const idsSaleChannels = filterData.channels ? filterData.channels.value : undefined;
                        const idsCompanySize = filterData.companySizes ? filterData.companySizes.value : undefined;
                        const idsSalesType = filterData.salesType ? filterData.salesType.value : undefined;

                        LayerControls(name).updateLegend(features[0].properties.Tipo, period);
                        resolve({
                            destroy: true,
                            type: 'FeatureCollection',
                            features: _.map(features, f => {
                                const geo = f.properties.Geocodigo;

                                const matched = values.filter(x => x.geoCode === geo);

                                if (!matched.length) {
                                    f.properties.Value = 0;
                                    return f;
                                }

                                f.properties.DncIdLocale = matched[0].idLocalidade;
                                f.properties.DncValues = matched;

                                const filtered = matched.filter(x =>
                                    (!segments || segments.includes(x.nameSegment.toUpperCase()))
                                    && (!idsSaleChannels || idsSaleChannels.includes(x.idSaleChannel))
                                    && (!idsCompanySize || idsCompanySize.includes(x.companySize))
                                    && (!idsSalesType || idsSalesType.includes(x.idSaleType)));

                                const dncValuesBySegment = _.groupBy(filtered, 'nameSegment');

                                const totalDnc = Object.values(dncValuesBySegment)
                                    .map(group =>
                                        group.reduce((sum, item) => sum + (item.dncValue || 0), 0)
                                    )
                                    .reduce((acc, val) => acc + Math.round(val), 0);

                                f.properties.Value = totalDnc || 0;

                                return f;
                            })
                        });
                    });
                });

        const onClick = (feature, layer) => {
            $(`#DncFlexible-selection-${feature.properties.Id}`).iCheck(LayerInfo(name).check(feature.properties.Id) ? 'uncheck' : 'check');
        };

        const onComplete = () => {
            LayerControls(name).show({
                show: false,
                refresh: true
            });
            if (store.getters[OVERVIEW_PANEL_GET_CURRENT] === name && store.getters[OVERVIEW_PANEL_IS_VISIBLE] && LayerControls(name).getControls(mapId).button.overview.enable) {
                LayerControls(name).toggleOverview();
            }
            domain = [];
        };

        const inverse = true;
        const ignore = (value) => (Number(value) > 0);
        const layer = new Leaflet.GeoJSONLayer();
        const localeTypes = environment.get('CONFIG.FILTERS.DNC_FLEXIBLE_TYPES');

        layer.setOptions({
            map: currentMap,
            remote: {
                url: `${environment.get('API_HOST')}/localidade/GetLocation`,
                params: {
                    locationType: [localeTypes].findOrFirst(_ => true).id,
                    format: 'topojson'
                }
            },
            filter: {
                location: true,
                properties: {
                    Value: '!0'
                }
            },
            cache: true,
            scaleRange: new Leaflet.ZoomLevel(layer, {
                id: [localeTypes].findOrFirst(_ => true).id,
                property: 'locationType',
                areas: environment.get('CONFIG.FILTERS.DNC_FLEXIBLE_TYPES')
            }),
            expandView: 0.3,
            showAll: false,
            uniqueField: 'Id',
            value: 'Value',
            success: onSuccess,
            done: onComplete,
            click: onClick,
            sort: 'Nome',
            label: {
                template (properties) {
                    if (properties.Nome) {
                        return templates('map-layer-areas-label', properties);
                    } else {
                        return '';
                    }
                },
                options: {
                    offset: [12, -10],
                    direction: 'right'
                }
            },
            type: 'cluster_area',
            symbology: {
                type: 'choropleth',
                scale: 'jenks',
                dualband: true,
                theme: ColorBrewer[ColorBrewer.RED_WHITE_BLUE][9],
                cluster: {
                    theme: ColorBrewer[ColorBrewer.RED_WHITE_BLUE][9],
                    template: clusterTemplate,
                    force: true
                },
                heat: {
                    theme: ColorBrewer.getHeatColors(ColorBrewer.RED_WHITE_BLUE),
                    property: 'Value',
                    scale: 'jenks',
                    inverse,
                    ignore,
                    dualband: true
                },
                style: {
                    lineJoin: 'round',
                    highlight: ColorBrewer[ColorBrewer.RED_WHITE_BLUE][9][8],
                    color: ColorFlatUI.wet_asphalt[1][0],
                    fillColor: 'transparent',
                    fillOpacity: 0.4,
                    weight: 1.2
                }
            }
        });
        return layer;
    }
}

export default DncFlexibleLayer;



// WEBPACK FOOTER //
// ./src/scripts/layers/DncFlexibleLayer.js